import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGasI18n } from '../../shared/kit';
import { fetchPublicPlans } from './api';
import type { PlanCard, PlansListInitData } from './types';

interface Props {
    initData: PlansListInitData;
}

function formatPrice(price: number | null, locale: string): string | null {
    if (price === null || price === undefined) {
        return null;
    }
    return new Intl.NumberFormat(locale === 'en' ? 'en-CH' : 'fr-CH', {
        style: 'currency',
        currency: 'CHF',
    }).format(price);
}

function PlanCardView({ plan, locale }: { plan: PlanCard; locale: string }): React.ReactElement {
    const { t } = useGasI18n();
    const priceLabel = formatPrice(plan.price, locale);
    const periodLabel = t(`membership.plan.period_kinds.${plan.period_kind}`);

    return (
        <article className="gas-membership-plans-list__card">
            {plan.image_url && (
                <a className="gas-membership-plans-list__card-media" href={plan.plan_url}>
                    <img
                        className="gas-membership-plans-list__card-image"
                        src={plan.image_url}
                        alt={plan.name}
                        loading="lazy"
                    />
                </a>
            )}

            <header className="gas-membership-plans-list__card-header">
                <h2 className="gas-membership-plans-list__card-title">
                    <a className="gas-membership-plans-list__card-link" href={plan.plan_url}>
                        {plan.name}
                    </a>
                </h2>
                <p className="gas-membership-plans-list__card-meta">
                    {priceLabel && <span className="gas-membership-plans-list__price">{priceLabel}</span>}
                    <span className="gas-membership-plans-list__period">{periodLabel}</span>
                    {plan.is_recurring && (
                        <span className="gas-membership-plans-list__badge">{t('membership.plan.recurring')}</span>
                    )}
                </p>
            </header>

            {plan.intro && (
                <p className="gas-membership-plans-list__card-description">{plan.intro}</p>
            )}

            {plan.capabilities.length > 0 && (
                <ul className="gas-membership-plans-list__features">
                    {plan.capabilities.map((cap) => (
                        <li key={cap.key} className="gas-membership-plans-list__feature">
                            {t(cap.label_key)}
                        </li>
                    ))}
                </ul>
            )}

            {plan.requires_cotisation && (
                <p className="gas-membership-plans-list__notice">{t('membership.plan.requires_cotisation')}</p>
            )}

            <div className="gas-membership-plans-list__card-actions">
                <a className="gas-button gas-button--primary" href={plan.buy_url}>
                    {t('membership.index.subscribe')}
                </a>
                <a className="gas-button gas-button--outline" href={plan.plan_url}>
                    {t('membership.index.details')}
                </a>
            </div>
        </article>
    );
}

export default function App({ initData }: Props): React.ReactElement {
    const { t } = useGasI18n();

    const { data: plans } = useQuery({
        queryKey: ['membership-public-plans', initData.locale],
        queryFn: () => fetchPublicPlans(initData),
        initialData: initData.plans,
        staleTime: 5 * 60 * 1000,
    });

    const list = plans ?? initData.plans;

    return (
        <section className="gas-membership-plans-list">
            <header className="gas-membership-plans-list__header">
                <h1 className="gas-membership-plans-list__title">{t('membership.index.title')}</h1>
                <p className="gas-membership-plans-list__subtitle">{t('membership.index.subtitle')}</p>
            </header>

            {list.length === 0 ? (
                <p className="gas-membership-plans-list__empty">{t('membership.index.empty')}</p>
            ) : (
                <div className="gas-membership-plans-list__grid">
                    {list.map((plan) => (
                        <PlanCardView key={plan.id} plan={plan} locale={initData.locale} />
                    ))}
                </div>
            )}

            {initData.advisor_url && (
                <div className="gas-membership-plans-list__advisor">
                    <a className="gas-button gas-button--secondary" href={initData.advisor_url}>
                        {t('membership.index.advisor_cta')}
                    </a>
                </div>
            )}
        </section>
    );
}
